/**
 * Mastery-lite (CL-151, roadmap H2.3): client-side per-concept running score.
 *
 * The 10%-of-CSM-machinery mastery model from the H2.3 design
 * (docs/plans/H2.3-ADAPTIVE-ENGINE-V1.md §4): per concept id, an EWMA of
 * scored outcomes plus raw counters. It RECORDS learning signal; nothing
 * routes on it in v1 — branch decisions are per-question (lesson-engine
 * on_pass/on_fail). Thresholded routing (the vision doc's 0.80 Guide→Release
 * gate) waits for H2.4/H5.1 calibration data.
 *
 * Persistence: localStorage keyed by classroom identity (CL-145 — class code
 * + anonymous seat handle, zero PII). D1 already receives every per-step
 * concept outcome via the CL-146 reporter, so server-side mastery can always
 * be recomputed; localStorage is a fast client cache, not a source of truth.
 *
 * Pure functions + fail-soft storage helpers. No engine imports — main.js
 * wires this to the engine's 'question-answered' event.
 */
import { createLogger } from '../utils/logger.js'

const log = createLogger('mastery-lite')

/** EWMA weight for the newest observation. */
export const MASTERY_ALPHA = 0.3

export const MASTERY_STORAGE_PREFIX = 'ev:mastery:'

/**
 * Map a CL-139 verdict to an observation value, or null for outcomes that
 * are NOT observations ('review' is neutral fail-closed grading; 'skipped'
 * is absence of evidence — neither may move a mastery estimate).
 * @param {string} verdict
 * @returns {number|null}
 */
export function verdictValue(verdict) {
  if (verdict === 'correct') return 1
  if (verdict === 'partial') return 0.5
  if (verdict === 'incorrect') return 0
  return null
}

/** @returns {Object} empty mastery store: { [conceptId]: { score, attempts, correct } } */
export function createMasteryStore() {
  return {}
}

/**
 * Fold one scored outcome into the store for every tagged concept.
 * Mutates and returns the store. Non-observations and bad ids are no-ops.
 *
 * @param {Object} store - mastery store (createMasteryStore shape)
 * @param {string[]} conceptIds - concept ids the step was tagged with
 * @param {string} verdict - CL-139 verdict
 * @param {number} [alpha] - EWMA weight override (tests)
 */
export function updateMastery(store, conceptIds, verdict, alpha = MASTERY_ALPHA) {
  const value = verdictValue(verdict)
  if (value === null || !store || !Array.isArray(conceptIds)) return store

  for (const raw of conceptIds) {
    if (typeof raw !== 'string' || !raw.trim()) continue
    const id = raw.trim()
    const entry = store[id] || { score: null, attempts: 0, correct: 0 }
    // First observation seeds the estimate; later ones decay exponentially.
    entry.score = entry.score === null ? value : alpha * value + (1 - alpha) * entry.score
    entry.attempts += 1
    if (verdict === 'correct') entry.correct += 1
    store[id] = entry
  }
  return store
}

/**
 * @param {Object} store
 * @param {string} conceptId
 * @returns {{ score: number, attempts: number, correct: number }|null}
 */
export function getMastery(store, conceptId) {
  return (store && store[conceptId]) || null
}

/**
 * Lowest-scoring concepts with at least minAttempts observations — input for
 * a "concepts to practice" hint (summary panel, teacher dashboard).
 * @returns {Array<{ concept: string, score: number, attempts: number }>}
 */
export function weakestConcepts(store, { limit = 3, minAttempts = 1 } = {}) {
  if (!store) return []
  return Object.entries(store)
    .filter(([, e]) => e.attempts >= minAttempts && typeof e.score === 'number')
    .map(([concept, e]) => ({ concept, score: e.score, attempts: e.attempts }))
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
}

// ── Persistence (fail-soft, CL-145 identity) ─────────────────

/**
 * Storage key scoped to classroom identity. Anonymous (no class joined)
 * sessions share a device-local 'anon' bucket — still zero PII.
 */
export function masteryStorageKey(classCode, handle) {
  const safe = (s) => (typeof s === 'string' && s.trim() ? s.trim() : 'anon')
  return `${MASTERY_STORAGE_PREFIX}${safe(classCode)}:${safe(handle)}`
}

function safeStorage() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    return null
  }
}

/** Load a persisted store (empty store on anything unreadable). */
export function loadMastery(key) {
  const storage = safeStorage()
  if (!storage) return createMasteryStore()
  try {
    const parsed = JSON.parse(storage.getItem(key) || '{}')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : createMasteryStore()
  } catch {
    return createMasteryStore()
  }
}

/** Persist the store. Fail-soft — a storage error must never break a lesson. */
export function saveMastery(key, store) {
  const storage = safeStorage()
  if (!storage || !store) return
  try {
    storage.setItem(key, JSON.stringify(store))
  } catch (err) {
    log.warn('could not persist mastery store (ignored)', err)
  }
}
